// src/pages/PatientStatements.js
// Generate and send patient balance statements via email

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts';
import * as awsApi from '../services/awsApiService';
import {
  Plus, Mail, RefreshCw, CheckCircle, AlertTriangle,
  FileText, Send, Clock, DollarSign, X, User
} from 'lucide-react';

const STATUS_STYLES = {
  Draft: 'bg-slate-100 text-slate-600',
  Sent:  'bg-green-100 text-green-700',
  Paid:  'bg-teal-100 text-teal-700',
  Overdue: 'bg-red-100 text-red-700',
};

const formatCurrency = (v) =>
  v != null ? `$${parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—';

const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

export default function PatientStatements() {
  const navigate = useNavigate();
  const { practice } = useAuth();
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [sendingLinkId, setSendingLinkId] = useState(null);
  const [toast, setToast] = useState(null);
  const [patients, setPatients] = useState([]);
  const [claims, setClaims] = useState([]);

  const [form, setForm] = useState({
    patient_id: '', patient_name: '', patient_email: '',
    balance_amount: '', due_date: '', notes: '', selected_claims: []
  });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const [stmtRes, ptRes, clRes] = await Promise.all([
      awsApi.getStatements(),
      awsApi.getPatients(practice?.practice_id, { limit: 200 }),
      awsApi.getClaims(practice?.practice_id, { limit: 200 })
    ]);
    if (stmtRes.success) setStatements(stmtRes.data);
    if (ptRes.success) setPatients(ptRes.data || []);
    if (clRes.success) setClaims(clRes.data || []);
    setLoading(false);
  }, [practice]);

  useEffect(() => { load(); }, [load]);

  const handlePatientSelect = (e) => {
    const name = e.target.value;
    const pt = patients.find(p => `${p.first_name} ${p.last_name}` === name);
    if (pt) {
      const ptClaims = claims.filter(c =>
        (c.patient_id === pt.id || c.patient_id === pt.open_dental_patient_id?.toString()) &&
        c.claim_status === 'Paid' && parseFloat(c.claim_amount || 0) > parseFloat(c.claim_paid_amount || 0)
      );
      const balance = ptClaims.reduce((s, c) => s + (parseFloat(c.claim_amount || 0) - parseFloat(c.claim_paid_amount || 0)), 0);
      setForm(f => ({
        ...f,
        patient_id: pt.id,
        patient_name: name,
        patient_email: pt.email || '',
        balance_amount: balance.toFixed(2),
        selected_claims: ptClaims.map(c => c.id)
      }));
    } else {
      setForm(f => ({ ...f, patient_name: name, patient_id: '' }));
    }
  };

  const handleGenerate = async () => {
    if (!form.patient_id || !form.balance_amount) return showToast('Select a patient and enter balance', 'error');
    const res = await awsApi.generateStatement({
      patient_id: form.patient_id,
      patient_name: form.patient_name,
      patient_email: form.patient_email,
      balance_amount: form.balance_amount,
      claim_ids: form.selected_claims,
      due_date: form.due_date || null,
      notes: form.notes
    });
    if (res.success) {
      showToast('Statement created');
      setShowNewForm(false);
      setForm({ patient_id: '', patient_name: '', patient_email: '', balance_amount: '', due_date: '', notes: '', selected_claims: [] });
      load();
    } else {
      showToast(res.error, 'error');
    }
  };

  const handleSendPaymentLink = async (stmt) => {
    if (!stmt.patient_email) return showToast('No email on file for this patient', 'error');
    setSendingLinkId(stmt.id);
    const res = await awsApi.sendPaymentLink({
      statement_id: stmt.id,
      patient_name: stmt.patient_name,
      patient_email: stmt.patient_email,
      amount: stmt.balance_amount,
      description: `Patient Balance — ${stmt.patient_name}`
    });
    setSendingLinkId(null);
    if (res.success) {
      showToast('Payment link sent to patient');
      load();
    } else {
      showToast(res.error || 'Failed to send payment link', 'error');
    }
  };

  const handleSend = async (id) => {
    setSendingId(id);
    const res = await awsApi.sendStatement(id);
    setSendingId(null);
    if (res.success) {
      showToast(res.message || 'Statement sent');
      load();
    } else {
      showToast(res.error, 'error');
    }
  };

  const totalBalance = statements.filter(s => s.status !== 'Paid').reduce((s, st) => s + parseFloat(st.balance_amount || 0), 0);
  const sentCount = statements.filter(s => s.status === 'Sent').length;

  return (
    <div className="min-h-screen bg-slate-50">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2
          ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`}>
          {toast.type === 'error' ? <AlertTriangle size={16} /> : <CheckCircle size={16} />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Mail size={18} className="text-blue-700" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-slate-900">Patient Statements</h1>
              <p className="text-xs text-slate-500">Generate and send patient balance statements</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setShowNewForm(true)}
            className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-800">
            <Plus size={16} /> New Statement
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Outstanding Balance</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{formatCurrency(totalBalance)}</p>
            <p className="text-xs text-slate-400 mt-1">{statements.filter(s => s.status !== 'Paid').length} open statements</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Statements Sent</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{sentCount}</p>
            <p className="text-xs text-slate-400 mt-1">Emailed to patients</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Total Statements</p>
            <p className="text-2xl font-bold text-slate-700 mt-1">{statements.length}</p>
            <p className="text-xs text-slate-400 mt-1">All time</p>
          </div>
        </div>

        {/* New Statement Form */}
        {showNewForm && (
          <div className="bg-white rounded-xl border border-blue-200 shadow-sm">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                <Plus size={16} className="text-blue-600" /> New Patient Statement
              </h2>
              <button onClick={() => setShowNewForm(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Patient *</label>
                  <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.patient_name}
                    onChange={handlePatientSelect}
                    list="stmt-patient-list"
                    placeholder="Start typing patient name..." />
                  <datalist id="stmt-patient-list">
                    {patients.map(p => <option key={p.id} value={`${p.first_name} ${p.last_name}`} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Patient Email</label>
                  <input type="email" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.patient_email} onChange={e => setForm(f => ({ ...f, patient_email: e.target.value }))}
                    placeholder="patient@email.com" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Balance Amount *</label>
                  <input type="number" step="0.01" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.balance_amount} onChange={e => setForm(f => ({ ...f, balance_amount: e.target.value }))}
                    placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Due Date</label>
                  <input type="date" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-700 mb-1">Notes (shown on statement)</label>
                  <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="e.g. Insurance applied, remaining balance is patient responsibility" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowNewForm(false)}
                  className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                <button onClick={handleGenerate}
                  className="px-4 py-2 text-sm bg-blue-700 text-white rounded-lg hover:bg-blue-800 font-medium">
                  Generate Statement
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Statements Table */}
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-5 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">Statements</h2>
          </div>
          {loading ? (
            <div className="p-12 text-center text-slate-500">
              <RefreshCw size={24} className="animate-spin mx-auto mb-2 text-blue-600" />Loading...
            </div>
          ) : statements.length === 0 ? (
            <div className="p-12 text-center">
              <FileText size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium">No statements generated yet</p>
              <p className="text-slate-400 text-sm mt-1">Create your first patient statement</p>
              <button onClick={() => setShowNewForm(true)}
                className="mt-4 bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-800">
                New Statement
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Patient</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Email</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Balance</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Due Date</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Sent</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {statements.map(s => (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-slate-100 rounded-full flex items-center justify-center">
                          <User size={13} className="text-slate-500" />
                        </div>
                        <span className="text-sm font-medium text-slate-800">{s.patient_name || '—'}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-500">{s.patient_email || '—'}</td>
                    <td className="px-5 py-4 text-right font-bold text-slate-800">{formatCurrency(s.balance_amount)}</td>
                    <td className="px-5 py-4 text-sm text-slate-500">{formatDate(s.due_date)}</td>
                    <td className="px-5 py-4">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_STYLES[s.status] || 'bg-slate-100 text-slate-600'}`}>
                        {s.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-500">{s.sent_at ? formatDate(s.sent_at) : '—'}</td>
                    <td className="px-5 py-4">
                      {s.patient_email && s.status !== 'Paid' ? (
                        <div className="flex items-center gap-2">
                          {s.status === 'Draft' && (
                            <button
                              onClick={() => handleSend(s.id)}
                              disabled={sendingId === s.id}
                              className="flex items-center gap-1 bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-blue-800 disabled:opacity-50"
                            >
                              {sendingId === s.id ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
                              Statement
                            </button>
                          )}
                          <button
                            onClick={() => handleSendPaymentLink(s)}
                            disabled={sendingLinkId === s.id}
                            className="flex items-center gap-1 bg-teal-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-teal-700 disabled:opacity-50"
                          >
                            {sendingLinkId === s.id ? <RefreshCw size={12} className="animate-spin" /> : <DollarSign size={12} />}
                            Pay Link
                          </button>
                        </div>
                      ) : !s.patient_email ? (
                        <span className="text-xs text-amber-600 flex items-center gap-1">
                          <AlertTriangle size={11} /> No email
                        </span>
                      ) : (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle size={11} /> Paid
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
